<template>
  <sc-render :fields="fields" :empty-message="$t('NO_DATA_SOURCE_EXIST')">
    <component :is="componentType" v-bind="{ ...propTypes, ...componentClasses }">
      <div class="image-block">
        <media-display
          :sc-image-field="imageField"
          :image-desktop-source="imageSrc"
          :fraction="fraction"
        />
      </div>
      <div class="content-block" :class="{ 'champagne-background': themeOverride }">
        <heading
          class="title"
          desktopSize="size-2"
          mobileSize="size-5"
          :tagField="headingSizeField"
          :textField="titleField"
          :use-initial-color="themeOverride"
        />
        <div v-if="showDescriptionBlock" class="description-block">
          <sc-text
            v-if="this.isEditMode || this.description"
            class="description"
            tag="p"
            :field="descriptionField"
          />
          <sc-text
            v-if="this.isEditMode || this.tag"
            class="tag"
            tag="p"
            :field="tagField"
          />
        </div>
        <editorial-cta
          class="controls"
          :fields="fields"
          :use-plain-buttons="isOnlyOneCTA"
          :theme-override="themeOverride"
        />
      </div>
    </component>
  </sc-render>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Image, Text } from '@sitecore-jss/sitecore-jss-vue'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import ShLink from '~/components/sh-link'
import ScRender from '../sc-render'
import Heading from '../heading'
import EditorialCta from '../editorial-cta'
import MediaDisplay from '~/components/sitecore/media-display'
import fraction from '~/constants/fraction'
import ThemeMixin from '~/mixins/theme'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'EditorialPortraitImage',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    ScRender,
    ScImage: Image,
    ScText: Text,
    ShLink,
    Heading,
    EditorialCta,
    MediaDisplay
  },

  mixins: [ThemeMixin],

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      imageField: 'image',
      headingSizeField: 'headingSize',
      titleField: 'title',
      title: 'title.value',
      descriptionField: 'description',
      description: 'description.value',
      tagField: 'tag',
      tag: 'tag.value',
      cta1Href: 'cta1.value.href',
      cta1Target: 'cta1.value.target',
      cta2Href: 'cta2.value.href',
      cta2Target: 'cta2.value.target'
    }),

    isOnlyOneCTA () {
      return (this.cta1Href || this.cta2Href) && !(this.cta1Href && this.cta2Href)
    },
    componentType () {
      if (!this.isEditMode && this.isOnlyOneCTA) return 'sh-link'

      return 'div'
    },
    propTypes () {
      if (this.componentType === 'div') return {}

      return {
        to: this.cta1Href || this.cta2Href,
        target: this.cta1Target || this.cta2Target
      }
    },
    componentClasses () {
      return {
        class: [
          'editorial-portrait-image',
          {
            'theme-dark': this.isDarkTheme,
            'image-right': this.imagePosition === 'right'
          }
        ]
      }
    },
    imageSrc () {
      return get(this.imageField, 'value.cloudinary_3x4_fill_crop', '') || get(this.imageField, 'value.src', '')
    },
    imagePosition () {
      return get(this.fields, 'imagePosition.value', 'left')
    },
    backgroundChampagne () {
      return get(this.fields, 'backgroundChampagne.value', false)
    },
    themeOverride () {
      return !this.$device.isMobile && this.backgroundChampagne
    },
    showDescriptionBlock () {
      return this.isEditMode || this.description || this.tag
    },
    fraction () {
      return fraction.twoFr // see styles for width
    }
  }
}
</script>

<style lang="scss" scoped>
$full-width: 100%;
$half-width: $full-width / 2;

.editorial-portrait-image {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  @include stack-space($space-090);

  @include tablet {
    flex-direction: row;
    flex: 0 0 $full-width;
    @include stack-space($space-100);
  }

  &.image-right {
    @include tablet {
      flex-direction: row-reverse;
    }
  }

  &.theme-dark {
    .content-block .tag {
      color: $color-cream;
    }
  }

  .image-block {
    flex: 0 0 $half-width; // see computed for fraction
  }

  .content-block {
    flex: 0 0 $half-width;
    padding: $space-040 $space-060 0 $space-050;

    @include tablet {
      padding: $space-090 $space-080;
    }

    &.champagne-background {
      background-color: $color-cream;

      .description, .tag {
        color: $color-black;
      }
    }

    .title {
      @include font('060');

      @include tablet {
        @include font('100');
      }
    }

    .description-block {
      margin-top: $space-020;

      @include tablet {
        margin-top: $space-050;
      }
    }

    .description {
      @include font('050');
      @include stack-space(0);

      @include tablet {
        @include font('060');
      }
    }

    .tag {
      margin-top: $space-020;
      text-transform: uppercase;
      color: $color-slate-gray;
      @include font('030');
      @include stack-space(0);

      @include tablet {
        margin-top: $space-040;
      }
    }

    .controls {
      margin-top: $space-050;
    }
  }
}
</style>
